import { FC, useEffect, useRef } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { cloneDeep } from "lodash";
import { DsmButton, DsmIcon } from "@dsm-dcs/design-system-react";
import { BaselineDialogProps, FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import { StageType } from "../../../../../../graphql/types";
import { FacilityTableItem } from "../../../../models/Facility/FacilityTypes";
import {
  ShrimpBaseline,
  ShrimpProcessingInput,
  ShrimpProcessingInputType,
  ShrimpProcessingStageData,
  ShrimpStage,
} from "../../../../models/Baseline/ShrimpBaseline";
import ShrimpAnimalsFormSourceContent, { ShrimpUnitsEnum } from "./ShrimpAnimalsFormSourceContent";
import { GenericStage } from "../../../../models/Baseline";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { Spacer } from "../../../helpers/Spacer";

const DEFAULT_EMPTY_SOURCE = {
  transport: [],
  originOutputId: "",
  totalWeight: "",
  originStageId: "",
  quantity: undefined,
};

interface ShrimpAnimalsFormDialogProps
  extends BaselineDialogProps {
  // eslint-disable-next-line react/require-default-props
  formType: FormType;
  itemIndex: number;
  formVisible: boolean;
  formTitle: string;
  facility: FacilityTableItem | undefined;
  iconCode: string;
}

const ShrimpAnimalsFormDialog: FC<
  ShrimpAnimalsFormDialogProps
> = ({
  handleCancel,
  formType = FormType.Add,
  itemIndex = 0,
  formVisible = false,
  formTitle,
  facility,
  iconCode,
  baseline,
}) => {
    const intl = useIntl();

    const fieldItemPrefix = `stages.${itemIndex}.stageData.processingInput`;
    const currResetValue = useRef<ShrimpProcessingInput>();
    const formContext = useFormContext<ShrimpBaseline>();
    const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const barnSmallQuantityUnits = userUOM?.unitBarnSmallQuantityUnits ?? "g";

    const {
      fields: sources,
      append: appendSources,
      remove: removeSources,
    } = useFieldArray({
      name: `${fieldItemPrefix}.sources`,
      control: formContext.control,
      keyName: "keyId",
    });

    useEffect(() => {
      if (formVisible) {
        if (!sources?.length) appendSources(DEFAULT_EMPTY_SOURCE);

        currResetValue.current = cloneDeep<ShrimpProcessingInput>(
          formContext.getValues(fieldItemPrefix)
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formVisible]);

    const stages = useWatch({
      name: "stages",
      control: formContext.control,
    }) as GenericStage[];

    const sourcesWatch = useWatch({
      name: `${fieldItemPrefix}.sources`,
      control: formContext.control,
    }) as ShrimpProcessingInputType[];

    const getSelectedOutputsForStage = (stageId: string): string[] =>
      sourcesWatch
        ?.filter((source) => source.originStageId === stageId)
        .map((source) => source.originOutputId);

    const canAddAnotherSource = (): boolean => {
      const growingStages = stages?.filter(
        (stage) => stage.type === StageType.Growing
      ) as ShrimpStage[];

      return growingStages?.some((stage) => {
        const outputs = stage?.stageData?.output?.outputs ?? [];
        const selectedOutputs = getSelectedOutputsForStage(stage.id) ?? [];
        return outputs.some((output) => !selectedOutputs.includes(output.id));
      });
    };

    const handleResetClick = () => {
      if (currResetValue.current) {
        const resetObject = { ...formContext.getValues() };
        const stageData = resetObject.stages[itemIndex]
          ?.stageData as ShrimpProcessingStageData;
        if (stageData?.processingInput) {
          stageData.processingInput = currResetValue.current;
          formContext.reset(resetObject, { errors: true });
        }
      }

      if (handleCancel) handleCancel("reset");
    };

    const handleSourceRemoveClick = (index: number) => {
      removeSources(index);
    };

    return (
      <DialogContainer
        formVisible={formVisible}
        handleClose={handleResetClick}
        iconCode={iconCode}
        variant="wide"
        formTitle={formTitle}
        introText={intl.formatMessage({
          id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.DESCRIPTION",
        })}
      >
        <>
          <div style={{ maxWidth: "50%" }}>
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.averageWeight`}
              label={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.AVERAGE_WEIGHT",
              })}
              disabled={formType === FormType.View}
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.AVERAGE_WEIGHT.TOOLTIP",
              })}
              adornment={intl.formatMessage(
                {
                  id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.AVERAGE_WEIGHT.ADORNMENT",
                },
                {
                  unit: barnSmallQuantityUnits,
                }
              )}
              required
              type="number"
              defaultValue={formContext.getValues(
                `${fieldItemPrefix}.averageWeight`
              )}
            />
          </div>

          <Spacer />

          {sources?.map((source, index) => (
            <ShrimpAnimalsFormSourceContent
              key={`shrimp-source-${index}-${source.keyId}`}
              fieldItemPrefix={fieldItemPrefix}
              formType={formType}
              itemIndex={index}
              averageWeightUnit={
                barnSmallQuantityUnits === ShrimpUnitsEnum.Ounce
                  ? ShrimpUnitsEnum.Ounce
                  : ShrimpUnitsEnum.Grams
              }
              handleSourceRemoveClick={handleSourceRemoveClick}
            />
          ))}

          {canAddAnotherSource() && (
            <DsmButton
              variant="text"
              onClick={() => appendSources(DEFAULT_EMPTY_SOURCE)}
              disabled={formType === FormType.View}
              style={{
                width: "max-content",
              }}
            >
              <DsmIcon slot="before" name="general/plus-circle" />
              {intl.formatMessage({
                id: "SUSTELL.FACILITIES.RESOURCES.ADD_ANOTHER_SOURCE",
              })}
            </DsmButton>
          )}

          <DsmButtonControlGroup
            cancelHandler={handleResetClick}
            saveHandler={async () => {
              await formContext.trigger(
                `stages.${itemIndex}.stageData.processingInput`
              );
              handleCancel("confirm");
            }}
            saveLabel={intl.formatMessage({ id: "GENERAL.CONFIRM" })}
          />
        </>
      </DialogContainer>
    );
  };

export default ShrimpAnimalsFormDialog;
